import { defineComponent as _defineComponent } from 'vue'
import { reactive, inject, toRefs, watch, ref } from "vue";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import ReplyController from "@/controllers/communityTalk/replyController";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import PostProfilePicture from "@/components/communityTalk/PostProfilePicture.vue";
import LoginUtil from "@/modules/utils/loginUtil";
import PostPermissionUtil from "@/modules/utils/postPermissionUtil";
import RequireLogin from "@/components/communityTalk/RequireLogin.vue";
import PostComplete from "@/components/communityTalk/PostComplete.vue";
import PostReviewerAttribute from "@/components/communityTalk/PostReviewerAttribute.vue";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import PostNickName from "@/components/communityTalk/PostNickName.vue";
import ReplyPostContents from "@/components/communityTalk/reply/ReplyPostContents.vue";
import AppConst from "@/modules/const";
import ReplyPostMedia from "./ReplyPostMedia.vue";
import { LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import { ReplyPostStore, isValidReplyPostStoreKey } from "@/store/communityTalk/replyPostStore";

interface Props {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * ReplyPostStoreのInject用キー。
	 */
	replyPostStoreKey: string;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
}
interface State {
	/**
	 * APIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * APIバリデーションエラーオブジェクト。
	 */
	apiValidationErrorViewModel: ApiValidationErrorViewModel | null;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 内容が未入力かどうか。
	 */
	isContentsEmpty: boolean;
	/**
	 * ニックネームが未入力かどうか。
	 */
	isNickNameEmpty: boolean;
}

// reactiveな変数宣言

export default /*#__PURE__*/_defineComponent({
  props: {
    communityTalkStoreKey: { type: String, required: true },
    replyPostStoreKey: { type: String, required: true },
    wordSettings: { type: null, required: true }
  },
  setup(__props: any, { expose }) {
  expose()

const props = __props as {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * ReplyPostStoreのInject用キー。
	 */
	replyPostStoreKey: string;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
}
/**
 * リプライ投稿。
 */



// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey) && !isValidReplyPostStoreKey(props.replyPostStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}、${props.replyPostStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;
const replyPostStore = inject(props.replyPostStoreKey) as ReplyPostStore;

const state = reactive<State>({
	status: ApiPostLoadStatus.beforeRequest,
	apiValidationErrorViewModel: null,
	isProcessing: false,
	isContentsEmpty: false,
	isNickNameEmpty: false,
});
const { status, apiValidationErrorViewModel, isContentsEmpty, isNickNameEmpty } = toRefs(state);

/**
 * 投稿が許可されてるかどうか。
 */
const isPostable = PostPermissionUtil.isPostable(
	communityTalkStore.getGeneralSettings().replyPostPermissionSetting,
	communityTalkStore.getGeneralSettings().replyPostPermissionSpecificECMemberIds,
	communityTalkStore.getGeneralSettings().nonMemberIdList,
	communityTalkStore.getSiteUserId()
);
/**
 * 投稿フォームを表示するかどうか。
 */
const isFormVisible = PostPermissionUtil.isFormVisible(
	communityTalkStore.getGeneralSettings().replyPostPermissionSetting,
	communityTalkStore.getGeneralSettings().replyPostPermissionSpecificECMemberIds,
	communityTalkStore.getSiteUserId()
);

/**
 * ニックネームが必須かどうか。
 */
const isRequiredNickname = communityTalkStore.getGeneralSettings().nicknameRequiredSetting === "Required";

const replyController = new ReplyController();
/**
 * リプライ投稿処理。
 */
const post = async (): Promise<void> => {
	state.isProcessing = true;
	state.isContentsEmpty = false;
	state.isNickNameEmpty = false;

	// 内容が未入力の場合はエラーメッセージを表示
	if (replyPostStore.getContents() === "") {
		state.isContentsEmpty = true;
		state.isProcessing = false;
	}
	// 内容が閾値を超えている場合何もしない
	if (replyPostStore.getContents().length > AppConst.REPLY_CONTENTS_MAX_LENGTH) {
		state.isProcessing = false;
	}
	// ニックネームが必須設定で未入力の場合は何もしない
	if (isRequiredNickname && (communityTalkStore.getCommunityTalkUserNickName() == null || communityTalkStore.getCommunityTalkUserNickName() === "")) {
		state.isNickNameEmpty = true;
		state.isProcessing = false;
	}

	if (!state.isProcessing) return;

	// 画面が投稿フォーム下部に取りこのされてしまうため、投稿フォーム上部に移動する
	window.location.href = "#tr_" + replyPostStore.getTalkRoomId();

	state.status = ApiPostLoadStatus.loading;
	try {
		// SiteUserIdの送信可否を確認
		// 不可である場合、undefindを送信する。
		let siteUserId = communityTalkStore.getSiteUserId();
		if (!LoginUtil.canSendSiteUserId(siteUserId, communityTalkStore.getGeneralSettings().nonMemberIdList, communityTalkStore.getGeneralSettings().replyPostPermissionSetting)) {
			siteUserId = undefined;
		}

		await replyController.postReplyData(
			communityTalkStore.getCommunityTalkUserNickName(),
			communityTalkStore.getCommunityTalkUserReviewerAttributes(),
			communityTalkStore.getCommunityTalkUserProfilePicViewModel().profilePicIdTmp,
			replyPostStore.getPostMediaViewModels(),
			replyPostStore.getTalkRoomId(),
			communityTalkStore.getTalkRoomUrl(),
			communityTalkStore.getTenantId(),
			communityTalkStore.getTagCd(),
			replyPostStore.getContents(),
			communityTalkStore.getReviCoCookieId(),
			siteUserId
		);
		state.status = ApiPostLoadStatus.success;
		// LocalStorageにプリセット情報を保持
		communityTalkStore.setCommunityTalkViewModelToLocalStorage();
	} catch (e) {
		// バリデーションエラーであればエラーをを表示させ、もう1度入力できるようにする
		if ((e as ApiErrorViewModel).isValidationError) {
			state.apiValidationErrorViewModel = (e as ApiErrorViewModel).validationErrorContents;
			state.status = ApiPostLoadStatus.beforeRequest;
		} else {
			// そうでなければエラー扱い(入力はもう一度出来ない)
			state.status = ApiPostLoadStatus.error;
		}
	}
	state.isProcessing = false;
};

/**
 * 投稿欄の表示非表示切り替え処理。
 */
const toggleArea = (): void => {
	if (replyPostStore.getIsDisplayReplyPostArea()) {
		replyPostStore.setIsDisplayReplyPostArea(false);
	} else {
		replyPostStore.setIsDisplayReplyPostArea(true);
	}
};

const replyPostSection = ref<HTMLElement | null>(null);
/**
 * リプライ返信ボタンが押下されたら、スクロールする。
 */
watch(
	() => replyPostStore.getIsClickReplyResponse(),
	(isClickReplyResponse) => {
		// フラグが折られたときは動作しない
		if (!isClickReplyResponse) {
			return;
		}

		// 次にリプライ返信ボタンが押下された時にトリガーできるよう、フラグを折ってStore保存
		replyPostStore.setIsClickReplyResponse(false);

		if (!replyPostSection.value) {
			return;
		}
		// 要素の始点までスクロールされると、ECサイトの固定表示ヘッダーで見切れる可能性がある。
		// 始点から少し上の位置までスクロールする。
		const heightMargin = 200;
		const height = replyPostSection.value.getBoundingClientRect().top + window.pageYOffset;
		window.scrollTo({
			top: height - heightMargin,
			behavior: "smooth",
		});
	}
);

// 画像・動画投稿開始イベント検知処理
const onStartMediaPostProcess = (): void => {
	replyPostStore.setIsMediaPostProcessing(true);
};
// 画像・動画投稿終了イベント検知処理
const onEndMediaPostProcess = (): void => {
	replyPostStore.setIsMediaPostProcessing(false);
};

// リプライ投稿欄開閉ボタンが有効かどうか
const isEnabledFormOpenButton = (): boolean => {
	// 投稿が許可されてない場合は無効
	if (!isPostable) {
		return false;
	}
	// 画像・動画投稿中は無効
	// 画像・動画投稿中にコンポーネントを開閉すると、初期表示がおかしくなるため
	if (replyPostStore.getIsMediaPostProcessing()) {
		return false;
	}
	return true;
};

// 投稿ボタンが有効かどうか
const isEnabledPostButton = (): boolean => {
	// 投稿が許可されてない場合は無効
	if (!isPostable) {
		return false;
	}
	// 投稿処理中なら無効
	if (state.isProcessing) {
		return false;
	}
	// 画像・動画投稿中なら無効
	if (replyPostStore.getIsMediaPostProcessing()) {
		return false;
	}
	return true;
};

const __returned__ = { props, communityTalkStore, replyPostStore, state, status, apiValidationErrorViewModel, isContentsEmpty, isNickNameEmpty, isPostable, isFormVisible, isRequiredNickname, replyController, post, toggleArea, replyPostSection, onStartMediaPostProcess, onEndMediaPostProcess, isEnabledFormOpenButton, isEnabledPostButton, ApiPostLoadMaster, PostProfilePicture, RequireLogin, PostComplete, PostReviewerAttribute, PostNickName, ReplyPostContents, ReplyPostMedia }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})